import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtEUR, fmtDate } from "@/lib/api";
import CandidatureModal from "@/components/CandidatureModal";

/**
 * CandidaturesList · listado + summary anual de candidaturas (C1 Tracker).
 *
 * Props:
 *   - filterProjectId?  → filtra solo las del proyecto (modo "dentro del proyecto")
 *   - compact?          → vista resumida (Dashboard)
 *   - title?            → título del bloque
 *
 * Sin props → todas las candidaturas, modo /funding "Mis candidaturas".
 */
export default function CandidaturesList({ filterProjectId, compact = false, title = "Mis candidaturas" }) {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("ALL");
  const [editing, setEditing] = useState(null);
  const year = new Date().getFullYear();

  const load = async () => {
    setLoading(true);
    try {
      const params = filterProjectId ? `?project_id=${filterProjectId}` : "";
      const r = await api.get(`/candidatures${params}`);
      setRows(r.data || []);
      const s = await api.get(`/candidatures/summary?year=${year}`);
      setSummary(s.data || null);
    } catch (_e) { /* silencioso */ }
    setLoading(false);
  };
  useEffect(() => { load(); }, [filterProjectId]);

  const filtered = useMemo(() => {
    if (filterStatus === "ALL") return rows;
    return rows.filter((r) => r.status === filterStatus);
  }, [rows, filterStatus]);

  const counts = useMemo(() => {
    const c = { ALL: rows.length, preparando: 0, enviada: 0, esperando: 0, aprobada: 0, denegada: 0 };
    rows.forEach((r) => { c[r.status] = (c[r.status] || 0) + 1; });
    return c;
  }, [rows]);

  const remove = async (cand) => {
    if (!window.confirm(`¿Eliminar candidatura "${cand.project_title} → ${cand.call_title}"?`)) return;
    setRows((rs) => rs.filter((x) => x.id !== cand.id));
    try { await api.delete(`/candidatures/${cand.id}`); } catch (_e) { load(); }
  };

  return (
    <section data-testid="candidatures-list">
      {/* Header con summary */}
      {!compact && (
        <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="gb-eyebrow">Convocatorias presentadas</p>
            <h2 className="mt-1 gb-display text-2xl">{title}</h2>
            {summary && (
              <p className="mt-1 text-sm text-gb-muted">
                {summary.total} candidaturas en {year} · pedidos {fmtEUR(summary.amount_requested)} · conseguidos{" "}
                <span className="font-semibold text-gb-black">{fmtEUR(summary.amount_granted)}</span>
                {summary.win_rate > 0 && <> · <span className="font-semibold text-emerald-700">win rate {summary.win_rate}%</span></>}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Filtros por estado */}
      {!compact && rows.length > 0 && (
        <div className="mb-4 flex flex-wrap gap-1.5">
          <FilterPill label="Todas" count={counts.ALL} active={filterStatus === "ALL"} onClick={() => setFilterStatus("ALL")} testid="cf-ALL" />
          {[
            ["preparando", "Preparando", "#B8B5AD"],
            ["enviada", "Enviada", "#C9821B"],
            ["esperando", "Esperando", "#5B7A8E"],
            ["aprobada", "Aprobada", "#2E7D5B"],
            ["denegada", "Denegada", "#941620"],
          ].map(([k, lbl, hex]) => (
            counts[k] > 0 && (
              <FilterPill key={k} label={lbl} count={counts[k]} hex={hex}
                          active={filterStatus === k} onClick={() => setFilterStatus(k)} testid={`cf-${k}`} />
            )
          ))}
        </div>
      )}

      {/* Lista */}
      {loading && (
        <p className="rounded-2xl border border-gb-paper bg-gb-surface px-5 py-8 text-center text-sm text-gb-muted">
          Cargando…
        </p>
      )}
      {!loading && filtered.length === 0 && (
        <p className="rounded-2xl border border-gb-paper bg-gb-surface px-5 py-8 text-center text-sm text-gb-muted" data-testid="cand-empty">
          {rows.length === 0
            ? "Aún no has presentado candidaturas. Cuando estés en una convocatoria abierta, pulsa \"Presentar con este proyecto\"."
            : `Sin candidaturas en estado "${filterStatus}".`}
        </p>
      )}
      {!loading && filtered.length > 0 && (
        <div className="overflow-hidden rounded-2xl border border-gb-paper bg-gb-surface" data-testid="cand-rows">
          {filtered.map((c, idx) => (
            <CandidatureRow key={c.id} cand={c} isLast={idx === filtered.length - 1}
                            compact={compact}
                            showProject={!filterProjectId}
                            onEdit={() => setEditing(c)}
                            onRemove={() => remove(c)} />
          ))}
        </div>
      )}

      {editing && (
        <CandidatureModal mode="edit" candidature={editing}
                          onClose={() => setEditing(null)}
                          onSaved={() => { setEditing(null); load(); }} />
      )}
    </section>
  );
}

// ─── Row ─────────────────────────────────────────────────────────────────

const STATUS_META = {
  preparando: { label: "Preparando", bg: "bg-stone-200", text: "text-stone-700", dot: "#B8B5AD" },
  enviada: { label: "Enviada", bg: "bg-amber-100", text: "text-amber-900", dot: "#C9821B" },
  esperando: { label: "Esperando", bg: "bg-sky-100", text: "text-sky-900", dot: "#5B7A8E" },
  aprobada: { label: "Aprobada", bg: "bg-emerald-100", text: "text-emerald-900", dot: "#2E7D5B" },
  denegada: { label: "Denegada", bg: "bg-red-100", text: "text-red-900", dot: "#941620" },
};

function CandidatureRow({ cand, isLast, compact, showProject, onEdit, onRemove }) {
  const sty = STATUS_META[cand.status] || STATUS_META.preparando;
  return (
    <div className={`flex items-start gap-3 px-5 py-3 transition hover:bg-gb-paper-soft/40 ${!isLast ? "border-b border-gb-paper" : ""}`}
         data-testid={`cand-row-${cand.id}`}>
      <span className="mt-1 inline-block h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: sty.dot }} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <p className="truncate font-serif text-[15px] text-gb-black">{cand.call_title}</p>
          {cand.version_name && (
            <span className="rounded-full bg-gb-paper-soft px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-gb-black/70">
              {cand.version_name}
            </span>
          )}
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-gb-muted">
          {showProject && (
            <Link to={`/project/${cand.project_id}`} className="font-semibold text-gb-black/75 hover:text-gb-red">
              {cand.project_title}
            </Link>
          )}
          {cand.call_organism && <span>{cand.call_organism}</span>}
          {cand.sent_at && <span>◷ Enviada {fmtDate(cand.sent_at)}</span>}
          {cand.resolution_expected_at && !cand.resolution_at && (
            <span>◷ Resuelve {fmtDate(cand.resolution_expected_at)}</span>
          )}
          {cand.resolution_at && <span>◷ Resuelto {fmtDate(cand.resolution_at)}</span>}
        </div>
      </div>
      <div className="flex shrink-0 flex-col items-end gap-1 text-right">
        <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-widest ${sty.bg} ${sty.text}`}>
          {sty.label}
        </span>
        <span className="font-mono text-[11px] tabular-nums text-gb-black/80">
          {fmtEUR(cand.amount_requested)}
          {cand.status === "aprobada" && cand.amount_granted > 0 && (
            <span className="ml-1 text-emerald-700">→ {fmtEUR(cand.amount_granted)}</span>
          )}
        </span>
      </div>
      {!compact && (
        <div className="ml-2 flex shrink-0 gap-1">
          <button onClick={onEdit} data-testid={`cand-edit-${cand.id}`}
                  className="rounded-full border border-gb-paper px-2 py-1 text-[10px] font-semibold uppercase tracking-widest text-gb-black/70 hover:border-gb-red hover:text-gb-red">
            Editar
          </button>
          <button onClick={onRemove} data-testid={`cand-del-${cand.id}`}
                  className="rounded-full border border-gb-paper px-2 py-1 text-[10px] text-gb-muted hover:border-gb-red hover:text-gb-red">
            ×
          </button>
        </div>
      )}
    </div>
  );
}

const FilterPill = ({ label, count, hex, active, onClick, testid }) => (
  <button onClick={onClick} data-testid={testid}
          className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-semibold uppercase tracking-widest transition ${
            active ? "bg-gb-black text-white"
                   : "border border-gb-paper bg-gb-surface text-gb-black/65 hover:border-gb-black/30 hover:text-gb-black"
          }`}>
    {hex && <span className="h-1.5 w-1.5 rounded-full" style={{ background: hex }} />}
    <span>{label}</span>
    <span className={`tabular-nums text-[10px] ${active ? "text-white/65" : "text-gb-muted"}`}>{count}</span>
  </button>
);
